import { useCallback, useMemo, useEffect, useState, useRef } from 'react';
import { useWebSocket, useEvents } from './hooks';

function shortenPath(path: string): string {
  return path
    .replace(/^\/Users\/[^/]+\/Documents\/Code\//, '')
    .replace(/^\/Users\/[^/]+\//, '~/')
    .replace(/^\/home\/[^/]+\//, '~/');
}

function relativeTime(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 5) return 'now';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h`;
}

interface Turn {
  type: 'user' | 'claude';
  content: string;
  thinking?: string;
  timestamp: number;
  actions?: { tool: string; target: string }[];
  question?: { text: string; options: string[] };
}

const API_BASE = import.meta.env.VITE_API_URL || '';

export default function App() {
  const { events, addEvent } = useEvents({ initialFetch: true, limit: 300 });
  const { status } = useWebSocket({ onEvent: useCallback((e: Parameters<typeof addEvent>[0]) => addEvent(e), [addEvent]) });
  const [, setTick] = useState(0);
  const [showThinking, setShowThinking] = useState(false);
  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [events.length]);

  const { turns, isActive, currentAction, pendingQuestion } = useMemo(() => {
    const now = Date.now();
    const turnList: Turn[] = [];
    let currentTurn: Turn | null = null;
    let pendingQuestion: Turn['question'] | null = null;

    for (const e of events) {
      // User prompt
      if ((e.hook_event_name === 'UserPromptSubmit' && e.prompt) ||
          (e.hook_event_name === 'UserMessage' && e.message)) {
        if (currentTurn && currentTurn.type === 'claude') {
          turnList.push(currentTurn);
        }
        turnList.push({
          type: 'user',
          content: e.prompt || e.message || '',
          timestamp: e.timestamp || now,
        });
        currentTurn = {
          type: 'claude',
          content: '',
          timestamp: e.timestamp || now,
          actions: [],
        };
        pendingQuestion = null; // User responded, clear question
      }
      // Claude's text response
      else if (e.hook_event_name === 'AssistantMessage' && e.message) {
        if (currentTurn?.type === 'claude') {
          currentTurn.content = e.message;
          currentTurn.thinking = e.thinking;
          currentTurn.timestamp = e.timestamp || now;
        }
      }
      // AskUserQuestion - extract options
      else if (e.tool_name === 'AskUserQuestion' && e.tool_input) {
        const input = e.tool_input as { questions?: Array<{ question: string; options?: Array<{ label: string }> }> };
        const q = input.questions?.[0];
        if (q) {
          const question = {
            text: q.question,
            options: q.options?.map(o => o.label) || [],
          };
          if (currentTurn?.type === 'claude') {
            currentTurn.question = question;
          }
          pendingQuestion = question;
        }
      }
      // Tool use
      else if (e.tool_name && currentTurn?.type === 'claude') {
        const target = e.tool_input?.file_path
          ? shortenPath(e.tool_input.file_path as string)
          : e.tool_input?.command
            ? (e.tool_input.command as string).slice(0, 40)
            : e.tool_input?.pattern as string || '';

        currentTurn.actions = currentTurn.actions || [];
        const last = currentTurn.actions[currentTurn.actions.length - 1];
        if (!last || last.tool !== e.tool_name || last.target !== target) {
          currentTurn.actions.push({ tool: e.tool_name, target });
        }
        currentTurn.timestamp = e.timestamp || now;
      }
      // Stop
      else if (e.hook_event_name === 'Stop') {
        if (currentTurn?.type === 'claude') {
          if (!currentTurn.content) {
            currentTurn.content = e.reason || '';
          }
          turnList.push(currentTurn);
          currentTurn = null;
        }
      }
    }

    if (currentTurn?.type === 'claude' && (currentTurn.actions?.length || currentTurn.content)) {
      turnList.push(currentTurn);
    }

    const lastEvent = events[events.length - 1];
    const lastTime = lastEvent?.timestamp || 0;
    const isActive = (now - lastTime) < 30000;

    let currentAction = '';
    if (lastEvent?.tool_name) {
      const file = lastEvent.tool_input?.file_path as string;
      if (file) currentAction = `${lastEvent.tool_name} ${shortenPath(file)}`;
      else currentAction = lastEvent.tool_name;
    }

    return { turns: turnList.slice(-30), isActive, currentAction, pendingQuestion };
  }, [events]);

  const sendInput = async (text: string) => {
    if (!text.trim() || sending) return;

    setSending(true);
    try {
      await fetch(`${API_BASE}/api/input`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: text.trim() }),
      });
      setInputText('');
    } catch (err) {
      console.error('Failed to send:', err);
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendInput(inputText);
  };

  const isConnected = status === 'connected';

  return (
    <div className="h-screen bg-[#f5f0e6] text-[#3d3d3d] font-mono flex flex-col overflow-hidden">
      {/* Header */}
      <header className="flex items-center justify-between p-4 border-b border-[#ddd5c8]">
        <div>
          <h1 className="text-sm tracking-[0.2em] font-semibold">CLAUDE</h1>
          <p className="text-xs text-[#8b8680]">code session</p>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={() => setShowThinking(t => !t)}
            className={`text-xs px-2 py-1 rounded ${showThinking ? 'bg-[#d4cfc2] text-[#5d5850]' : 'text-[#a09890]'}`}
          >
            thinking
          </button>
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${
              !isConnected ? 'bg-[#a0998f]' :
              isActive ? 'bg-[#6b8e5e] animate-pulse' : 'bg-[#c4975a]'
            }`} />
            <span className="text-xs text-[#8b8680]">
              {!isConnected ? 'offline' : isActive ? 'working' : 'idle'}
            </span>
          </div>
        </div>
      </header>

      {/* Current action */}
      {isActive && currentAction && (
        <div className="px-4 py-2 bg-[#ebe5d9] text-sm text-[#5d5850] border-b border-[#ddd5c8]">
          {currentAction}
        </div>
      )}

      {/* Conversation */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-6">
        {turns.length === 0 ? (
          <p className="text-[#a09890] text-sm py-8 text-center">waiting for session...</p>
        ) : (
          turns.map((turn, i) => (
            <div key={i}>
              {turn.type === 'user' ? (
                <div className="bg-[#ebe5d9] rounded-lg px-4 py-3 mr-8">
                  <p className="text-sm text-[#3d3d3d] whitespace-pre-wrap">{turn.content}</p>
                  <p className="text-xs text-[#a09890] mt-2">{relativeTime(turn.timestamp)}</p>
                </div>
              ) : (
                <div className="ml-4 border-l-2 border-[#d4cfc2] pl-4">
                  {showThinking && turn.thinking && (
                    <div className="mb-3 text-xs text-[#8b8680] bg-[#f0ebe0] rounded p-3 italic whitespace-pre-wrap max-h-40 overflow-y-auto">
                      {turn.thinking}
                    </div>
                  )}

                  {turn.actions && turn.actions.length > 0 && (
                    <div className="mb-3 space-y-1">
                      {turn.actions.slice(0, 10).map((a, j) => (
                        <div key={j} className="text-sm">
                          <span className="text-[#a07850]">{a.tool}</span>
                          {a.target && <span className="text-[#6b6560] ml-2">{a.target}</span>}
                        </div>
                      ))}
                      {turn.actions.length > 10 && (
                        <p className="text-xs text-[#a09890]">+{turn.actions.length - 10} more</p>
                      )}
                    </div>
                  )}

                  {turn.content && (
                    <p className="text-sm text-[#3d3d3d] whitespace-pre-wrap">{turn.content}</p>
                  )}

                  {turn.question && (
                    <div className="mt-3 p-3 bg-[#f0ebe0] rounded">
                      <p className="text-sm text-[#5d5850] mb-2">{turn.question.text}</p>
                      <div className="flex flex-wrap gap-2">
                        {turn.question.options.map((opt, j) => (
                          <button
                            key={j}
                            onClick={() => sendInput(opt)}
                            className="px-3 py-1.5 text-sm bg-[#e5ded2] hover:bg-[#d4cfc2] rounded transition-colors"
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <p className="text-xs text-[#a09890] mt-2">{relativeTime(turn.timestamp)}</p>
                </div>
              )}
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {/* Pending question quick actions */}
      {pendingQuestion && (
        <div className="px-4 py-3 bg-[#ebe5d9] border-t border-[#ddd5c8]">
          <p className="text-sm text-[#5d5850] mb-2">{pendingQuestion.text}</p>
          <div className="flex flex-wrap gap-2">
            {pendingQuestion.options.map((opt, j) => (
              <button
                key={j}
                onClick={() => sendInput(opt)}
                disabled={sending}
                className="px-3 py-1.5 text-sm bg-[#d4cfc2] hover:bg-[#c4bfb2] rounded transition-colors disabled:opacity-50"
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <form onSubmit={handleSubmit} className="p-4 border-t border-[#ddd5c8] bg-[#f5f0e6]">
        <div className="flex gap-2">
          <input
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder="Send to Claude..."
            disabled={sending}
            className="flex-1 px-3 py-2 bg-[#ebe5d9] border border-[#d4cfc2] rounded text-sm placeholder-[#a09890] focus:outline-none focus:border-[#a07850] disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={sending || !inputText.trim()}
            className="px-4 py-2 bg-[#a07850] text-[#f5f0e6] rounded text-sm hover:bg-[#8a6840] disabled:opacity-50 disabled:hover:bg-[#a07850] transition-colors"
          >
            {sending ? '...' : 'Send'}
          </button>
        </div>
      </form>
    </div>
  );
}
